import { useState, useRef } from 'react';
import { Stage, Layer, Image as KonvaImage } from 'react-konva';
import { RiArrowLeftRightLine, RiArrowUpDownLine } from '@remixicon/react';
import useImage from 'use-image';
import { stageFrameClass, toolApplyClass, toolCancelClass, toolChipClass } from './editorUi';

interface MirrorToolProps {
  image: string;
  onApply: (newImage: string) => void;
  onClose: () => void;
}

export function MirrorTool({ image, onApply, onClose }: MirrorToolProps) {
  const [scaleX, setScaleX] = useState(1);
  const [scaleY, setScaleY] = useState(1);
  const [imageElement] = useImage(image);
  const stageRef = useRef<any>(null);

  const mirrorHorizontal = () => {
    setScaleX(prev => prev * -1);
  };

  const mirrorVertical = () => {
    setScaleY(prev => prev * -1);
  };

  const applyMirror = () => {
    if (!imageElement) return;

    try {
      // Cria um canvas com as dimensões originais da imagem
      const naturalW = imageElement.naturalWidth || imageElement.width;
      const naturalH = imageElement.naturalHeight || imageElement.height;
      
      const canvas = document.createElement('canvas');
      canvas.width = naturalW;
      canvas.height = naturalH;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) return;
      
      // Move o contexto para o centro e aplica o espelhamento
      ctx.translate(naturalW / 2, naturalH / 2);
      ctx.scale(scaleX, scaleY);
      
      // Desenha a imagem com as dimensões originais
      ctx.drawImage(
        imageElement,
        -naturalW / 2,
        -naturalH / 2,
        naturalW,
        naturalH
      );
      
      // Converte canvas para base64
      const dataURL = canvas.toDataURL('image/jpeg', 0.9);
      onApply(dataURL);
      onClose();
    } catch (error) {
      console.error('Erro ao espelhar imagem:', error);
    }
  };

  // Calcula o tamanho do stage baseado na imagem
  const getStageSize = () => {
    if (!imageElement) return { width: 400, height: 300 };
    
    const maxWidth = window.innerWidth * 0.8;
    const maxHeight = window.innerHeight * 0.6;
    
    let width = imageElement.width;
    let height = imageElement.height;
    
    // Redimensiona mantendo proporção
    if (width > maxWidth) {
      height = (height * maxWidth) / width;
      width = maxWidth;
    }
    
    if (height > maxHeight) {
      width = (width * maxHeight) / height;
      height = maxHeight;
    }
    
    return { width, height };
  };

  const stageSize = getStageSize();

  return (
    <div className="w-full h-full flex flex-col items-center justify-center relative">
      <div className="w-full">
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Stage
              ref={stageRef}
              width={stageSize.width}
              height={stageSize.height}
              className={stageFrameClass}
            >
              <Layer>
                <KonvaImage
                  image={imageElement}
                  x={stageSize.width / 2}
                  y={stageSize.height / 2}
                  offsetX={imageElement ? imageElement.width / 2 : 0}
                  offsetY={imageElement ? imageElement.height / 2 : 0}
                  scaleX={scaleX * (stageSize.width / (imageElement?.width || 1))}
                  scaleY={scaleY * (stageSize.height / (imageElement?.height || 1))}
                />
              </Layer>
            </Stage>
            
            <div className="flex gap-2">
              <button
                type="button"
                onClick={mirrorHorizontal}
                className={toolChipClass}
              >
                <RiArrowLeftRightLine className="h-3.5 w-3.5" />
                Horizontal
              </button>

              <button
                type="button"
                onClick={mirrorVertical}
                className={toolChipClass}
              >
                <RiArrowUpDownLine className="h-3.5 w-3.5" />
                Vertical
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 absolute bottom-0 left-1/2 -translate-x-1/2">
          <button
            type="button"
            onClick={onClose}
            className={toolCancelClass}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={applyMirror}
            className={toolApplyClass}
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}